import React from "react";

import { Spinner } from "../../components/ui/spinner";
import { getRequestOrigin } from "../../utils/requestOrigin";

interface CustomAuthInstance {
  accessToken: string | null;
}

const customAuthInstance: CustomAuthInstance = {
  accessToken: null,
};

const CUSTOM_AUTH_TOKEN_KEY = "custom_auth_token";

const CUSTOM_AUTH_CODE_KEY = "custom_auth_code";

const extractCodeFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code");

  if (code) {
    const url = new URL(window.location.href);
    url.searchParams.delete("code");
    window.history.replaceState({}, "", url.toString());
  }

  return code;
};

const getStoredToken = () => {
  return sessionStorage.getItem(CUSTOM_AUTH_TOKEN_KEY);
};

const storeToken = (token: string) => {
  sessionStorage.setItem(CUSTOM_AUTH_TOKEN_KEY, token);
};

const getStoredCode = () => {
  return sessionStorage.getItem(CUSTOM_AUTH_CODE_KEY);
};

const storeCode = (code: string) => {
  sessionStorage.setItem(CUSTOM_AUTH_CODE_KEY, code);
};

const _getCustomAuthAccessToken = async () => {
  const storedToken = getStoredToken();
  if (storedToken) {
    return storedToken;
  }

  const code = extractCodeFromUrl() || getStoredCode();

  if (!code) {
    throw new Error("Authentication code not found");
  }

  storeCode(code);

  const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-request-origin": getRequestOrigin(),
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get access token: ${response.statusText}`);
  }

  const data = await response.json();
  const accessToken = data.accessToken;

  storeToken(accessToken);

  return accessToken;
};

const getIdTokenCustomAuth = async () => {
  return customAuthInstance.accessToken;
};

const initCustomAuth = async () => {
  try {
    const accessToken = await _getCustomAuthAccessToken();
    console.log("Custom auth access token obtained", accessToken);
    customAuthInstance.accessToken = accessToken;
  } catch (error) {
    console.error("Error refreshing custom auth token:", error);
    customAuthInstance.accessToken = null;
  }
};

const isAuthenticatedCustomAuth = async (): Promise<boolean> => {
  return !!customAuthInstance.accessToken;
};

const forceRefreshTokensCustomAuth = initCustomAuth;

const signoutCustomAuth = async () => {
  customAuthInstance.accessToken = null;
  sessionStorage.removeItem(CUSTOM_AUTH_TOKEN_KEY);
  sessionStorage.removeItem(CUSTOM_AUTH_CODE_KEY);
};

const AuthProviderCustomAuth: React.FunctionComponent<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const initAuth = async () => {
      try {
        await initCustomAuth();
      } catch (error) {
        console.error("Failed to initialize custom authentication:", error);
        setError("Authentication failed. Redirecting to home page...");

        // setTimeout(() => {
        //   window.location.href = import.meta.env.VITE_MAIN_URL || "/";
        // }, 2000);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  if (isLoading) {
    return <Spinner size="lg" className="mx-auto" />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return <>{children}</>;
};

export {
  AuthProviderCustomAuth,
  forceRefreshTokensCustomAuth,
  getIdTokenCustomAuth,
  getStoredCode,
  initCustomAuth,
  isAuthenticatedCustomAuth,
  signoutCustomAuth,
};

export type { customAuthInstance };
